'use client';

import { motion, useReducedMotion } from 'framer-motion';
import { Fragment } from 'react';

interface RevealTextProps {
  text: string;
  className?: string;
  /** 每个词的揭示间隔（秒） */
  stagger?: number;
  /** 作为普通元素时的语义标签 */
  as?: 'h2' | 'h3' | 'p' | 'span';
  delay?: number;
}

const wordVariants = {
  hidden: { opacity: 0, y: '0.6em' },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

/**
 * 逐词揭示的标题，对标参考站 GSAP SplitText 的文字进场。
 * 词随视口进入依次上浮显现；reduced-motion 直接静态渲染。
 */
export const RevealText = ({ text, className, stagger = 0.06, as = 'h2', delay = 0 }: RevealTextProps) => {
  const prefersReducedMotion = useReducedMotion();
  const words = text.split(' ');

  if (prefersReducedMotion) {
    const Tag = as;
    return <Tag className={className}>{text}</Tag>;
  }

  // 预生成稳定 key：标题可能有重复词，用「词@出现序号」避免以数组下标为 key。
  const seen = new Map<string, number>();
  const tokens = words.map((word, index) => {
    const occurrence = seen.get(word) ?? 0;
    seen.set(word, occurrence + 1);
    return {
      word,
      key: `${word}@${occurrence}`,
      custom: index * (stagger / 0.06) + delay / 0.06,
      trailingSpace: index < words.length - 1,
    };
  });

  const MotionTag = motion[as] as typeof motion.h2;

  return (
    <MotionTag
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.6 }}
      aria-label={text}
    >
      {tokens.map(({ word, key, custom, trailingSpace }) => (
        <Fragment key={key}>
          <span className="inline-block overflow-hidden align-bottom">
            <motion.span className="inline-block" variants={wordVariants} custom={custom} aria-hidden>
              {word}
            </motion.span>
          </span>
          {trailingSpace ? ' ' : null}
        </Fragment>
      ))}
    </MotionTag>
  );
};
